import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import routes from "routes/routes";
import {
  useCreateOrganizationSeriesMutation,
  useGetOrganizationSeriesOptionsQuery,
  usePreviewOrganizationSeriesMutation,
} from "api/account/accountApiSlice";
import { SeriesCreateResponse, SeriesPreviewOccurrence } from "api/account/types";
import AccountPageLayout from "../components/AccountPageLayout";
import { ORGANIZATION_ID_PARAM, makeOrganizationBookingsPath } from "../common/organizationAdmin";
import {
  SeriesForm,
  WEEKDAYS,
  defaultRange,
  seriesErrorKey,
  seriesFormProblem,
  seriesRequestBody,
} from "../common/organizationSeries";

const todayIso = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const EMPTY_FORM: SeriesForm = {
  groupId: "",
  weekdays: [],
  startTime: "",
  endTime: "",
  startDate: "",
  endDate: "",
  numberOfGuests: "",
  comment: "",
};

/**
 * "Book a series" for an organisation (series booking, step 11). Owners only;
 * the server decides everything that is a rule — which rooms, the season,
 * the dates and their conflicts, the coverage (ADR-0003 amendment, ADR-0020).
 *
 * Three states on one page: the pattern, the server's preview (conflicts are
 * shown and never bookable; free dates can be left out), and the result per
 * date. Every refusal is told apart — a failure never reads as "no dates".
 */
const OrganizationSeriesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const organizationId = String(params[ORGANIZATION_ID_PARAM] ?? "");

  const { data: options, isLoading, isError, error } = useGetOrganizationSeriesOptionsQuery(
    { organizationId },
    { skip: organizationId.length === 0 }
  );
  const [previewSeries, { isLoading: isPreviewing }] = usePreviewOrganizationSeriesMutation();
  const [createSeries, { isLoading: isCreating }] = useCreateOrganizationSeriesMutation();

  const [form, setForm] = React.useState<SeriesForm>(EMPTY_FORM);
  const [preview, setPreview] = React.useState<SeriesPreviewOccurrence[] | null>(null);
  const [leftOut, setLeftOut] = React.useState<string[]>([]);
  const [result, setResult] = React.useState<SeriesCreateResponse | null>(null);
  const [errorKey, setErrorKey] = React.useState<string | null>(null);

  // The organization is context: switching must not carry a half-built series.
  React.useEffect(() => {
    setForm(EMPTY_FORM);
    setPreview(null);
    setLeftOut([]);
    setResult(null);
    setErrorKey(null);
  }, [organizationId]);

  // Suggest the rest of the season once it is known, and the room when there
  // is only one — only into empty fields.
  React.useEffect(() => {
    if (!options) return;
    const range = defaultRange(options.season, todayIso());
    const onlyRoom = Array.isArray(options.rooms) && options.rooms.length === 1 ? options.rooms[0].groupId : "";
    setForm((prev) => ({
      ...prev,
      ...(prev.startDate || prev.endDate ? {} : range),
      ...(prev.groupId ? {} : { groupId: onlyRoom }),
    }));
  }, [options]);

  const update = (patch: Partial<SeriesForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    // A changed pattern invalidates the preview it produced.
    setPreview(null);
    setLeftOut([]);
    setErrorKey(null);
  };

  const toggleWeekday = (value: number) =>
    update({
      weekdays: form.weekdays.includes(value)
        ? form.weekdays.filter((d) => d !== value)
        : [...form.weekdays, value],
    });

  /** A free date the owner leaves out of the series, or takes back in. */
  const toggleLeftOut = (date: string) =>
    setLeftOut((prev) => (prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]));

  const formatDay = (date: string): string =>
    new Date(`${date}T12:00:00`).toLocaleDateString(i18n.language, {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const problem = seriesFormProblem(form);
  // A public holiday is never booked here (M9, decision 3): like a taken
  // date it is shown and left out, and only staff can book it.
  const isPublicHoliday = (o: SeriesPreviewOccurrence) => o.holiday?.defaultExcluded === true;
  // Taken dates and dates in another organisation's hall time (W2-1,
  // `outside_window`) are both shown and never booked.
  const conflicts = (preview ?? []).filter((o) => o.status !== "available").map((o) => o.date);
  const publicHolidays = (preview ?? []).filter(isPublicHoliday).map((o) => o.date);
  const toBook = (preview ?? []).filter(
    (o) => o.status === "available" && !isPublicHoliday(o) && !leftOut.includes(o.date),
  );
  /** Why a date that is not booked is left out: taken, another organisation's hall time, a public holiday. */
  const leftOutKey = (o: SeriesPreviewOccurrence): string => {
    if (o.status === "outside_window") return "account.organization_series.taken_window";
    if (o.status === "conflict") return "account.organization_series.taken";
    return "account.organization_series.holiday_left_out";
  };
  /** School holidays on which a hall time pauses say so (D7, Q3 A); the date stays ticked. */
  const holidayLabelKey = (o: SeriesPreviewOccurrence): string => {
    if (o.holiday?.kind === "public") return "account.organization_series.holiday_public";
    return o.holiday?.windowPaused
      ? "account.organization_series.holiday_school_paused"
      : "account.organization_series.holiday_school";
  };
  const holidayName = (o: SeriesPreviewOccurrence): string => {
    const wanted = i18n.language.slice(0, 2).toUpperCase();
    const names = o.holiday?.name ?? [];
    return (names.find((n) => n.language === wanted) ?? names.find((n) => n.language === "EN") ?? names[0])?.text ?? "";
  };

  const handlePreview = async () => {
    setErrorKey(null);
    try {
      const answer = await previewSeries({ organizationId, body: seriesRequestBody(form) }).unwrap();
      setPreview(Array.isArray(answer?.occurrences) ? answer.occurrences : []);
      setLeftOut([]);
    } catch (err) {
      setErrorKey(seriesErrorKey(err));
    }
  };

  const handleCreate = async () => {
    setErrorKey(null);
    try {
      // Conflicts are never sent as bookable: the guard would refuse them, and
      // the owner has already been shown why.
      const answer = await createSeries({
        organizationId,
        body: seriesRequestBody(form, [...conflicts, ...publicHolidays, ...leftOut]),
      }).unwrap();
      setResult(answer);
    } catch (err) {
      setErrorKey(seriesErrorKey(err));
    }
  };

  if (isLoading) {
    return (
      <AccountPageLayout title={t("account.organization_series.title")}>
        <p className="text-sm text-slate-600">{t("common.loading")}</p>
      </AccountPageLayout>
    );
  }

  const backToPlan = (
    <Button variant="outline" className="w-full" onClick={() => navigate(makeOrganizationBookingsPath(organizationId))}>
      {t("account.organization_series.back_to_plan")}
    </Button>
  );

  // Not an owner (404), or the options could not be loaded — never an empty form.
  if (isError || !options) {
    const status = (error as { status?: number } | undefined)?.status;
    return (
      <AccountPageLayout title={t("account.organization_series.title")}>
        <p className="text-sm text-slate-600">
          {status === 404
            ? t("account.organization_series.error.not_allowed")
            : t("account.organization_series.load_error")}
        </p>
        <Button variant="outline" onClick={() => navigate(routes.accountPath)}>
          {t("account.organization_admin.back_to_account")}
        </Button>
      </AccountPageLayout>
    );
  }

  const rooms = Array.isArray(options.rooms) ? options.rooms : [];

  if (!options.season || rooms.length === 0) {
    return (
      <AccountPageLayout title={t("account.organization_series.title")}>
        <p className="text-sm text-slate-600">
          {!options.season
            ? t("account.organization_series.no_season")
            : t("account.organization_series.no_rooms")}
        </p>
        {backToPlan}
      </AccountPageLayout>
    );
  }

  if (result) {
    const created = result.results.filter((r) => r.outcome === "created");
    const failed = result.results.filter((r) => r.outcome === "failed");
    return (
      <AccountPageLayout title={t("account.organization_series.title")}>
        <p className="text-sm text-foreground">
          {t("account.organization_series.result_created", { count: created.length })}
        </p>
        {failed.length > 0 && (
          <div className="space-y-1">
            <p className="text-sm text-foreground">
              {t("account.organization_series.result_failed", { count: failed.length })}
            </p>
            <ul className="text-sm text-slate-600">
              {failed.map((r) => (
                <li key={r.date}>
                  {formatDay(r.date)}
                  {" · "}
                  {r.reason === "occupied"
                    ? t("account.organization_series.reason_occupied")
                    : t("account.organization_series.reason_other")}
                </li>
              ))}
            </ul>
          </div>
        )}
        {created.length > 0 && (
          <p className="text-xs text-slate-600">
            {result.mailSent
              ? t("account.organization_series.mail_sent")
              : t("account.organization_series.mail_not_sent")}
          </p>
        )}
        {backToPlan}
      </AccountPageLayout>
    );
  }

  return (
    <AccountPageLayout title={t("account.organization_series.title")}>
      <p className="text-sm text-slate-600">
        {t("account.organization_series.intro", {
          start: formatDay(options.season.startDate),
          end: formatDay(options.season.endDate),
        })}
      </p>

      <div className="space-y-1">
        <label className="text-sm font-medium text-slate-700" htmlFor="org-series-room">
          {t("account.organization_series.room")}
        </label>
        <Select value={form.groupId} onValueChange={(groupId: string) => update({ groupId })}>
          <SelectTrigger id="org-series-room">
            <SelectValue placeholder={t("account.organization_series.room_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {rooms.map((room) => (
              <SelectItem key={room.groupId} value={room.groupId}>
                {room.name || t("common.not_applicable")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <fieldset className="space-y-1">
        <legend className="text-sm font-medium text-slate-700">{t("account.organization_series.weekdays")}</legend>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((day) => {
            const active = form.weekdays.includes(day.value);
            return (
              <Button
                key={day.value}
                type="button"
                size="sm"
                variant={active ? "default" : "outline"}
                aria-pressed={active}
                onClick={() => toggleWeekday(day.value)}
              >
                {t(`account.organization_series.weekday.${day.key}`)}
              </Button>
            );
          })}
        </div>
      </fieldset>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="org-series-start-time">
            {t("account.organization_series.start_time")}
          </label>
          <Input id="org-series-start-time" type="time" value={form.startTime} onChange={(e) => update({ startTime: e.target.value })} />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="org-series-end-time">
            {t("account.organization_series.end_time")}
          </label>
          <Input id="org-series-end-time" type="time" value={form.endTime} onChange={(e) => update({ endTime: e.target.value })} />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="org-series-start-date">
            {t("account.organization_series.start_date")}
          </label>
          <Input
            id="org-series-start-date"
            type="date"
            min={options.season.startDate}
            max={options.season.endDate}
            value={form.startDate}
            onChange={(e) => update({ startDate: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="org-series-end-date">
            {t("account.organization_series.end_date")}
          </label>
          <Input
            id="org-series-end-date"
            type="date"
            min={form.startDate || options.season.startDate}
            max={options.season.endDate}
            value={form.endDate}
            onChange={(e) => update({ endDate: e.target.value })}
          />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="org-series-guests">
            {t("account.organization_series.guests")}
          </label>
          <Input
            id="org-series-guests"
            type="number"
            min={1}
            inputMode="numeric"
            value={form.numberOfGuests}
            onChange={(e) => update({ numberOfGuests: e.target.value })}
          />
        </div>
        <div className="col-span-2 space-y-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="org-series-comment">
            {t("account.organization_series.comment")}
          </label>
          <Input id="org-series-comment" value={form.comment} onChange={(e) => update({ comment: e.target.value })} />
        </div>
      </div>

      {problem && preview === null && (
        <p className="text-xs text-slate-600">{t(`account.organization_series.problem.${problem}`)}</p>
      )}

      {errorKey && (
        <div className="rounded-md border border-foreground/15 px-3 py-2 text-sm text-foreground" role="alert">
          {t(`account.organization_series.error.${errorKey}`)}
        </div>
      )}

      {preview === null ? (
        <Button className="w-full" onClick={handlePreview} disabled={problem !== null || isPreviewing}>
          {isPreviewing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("account.organization_series.check_dates")}
        </Button>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-foreground">
            {t("account.organization_series.preview_summary", {
              count: preview.length,
              conflicts: conflicts.length,
            })}
          </p>
          <ul className="space-y-1">
            {preview.map((occurrence) => {
              const conflict = occurrence.status !== "available";
              const publicHoliday = isPublicHoliday(occurrence);
              const included = !conflict && !publicHoliday && !leftOut.includes(occurrence.date);
              return (
                <li
                  key={occurrence.date}
                  className="flex items-center justify-between gap-3 rounded-md bg-primary/5 px-3 py-2"
                >
                  <span className="flex flex-col">
                    <span className={conflict || !included ? "text-sm text-slate-600 line-through" : "text-sm text-foreground"}>
                      {formatDay(occurrence.date)}
                    </span>
                    {occurrence.holiday && (
                      <span className="text-xs text-slate-600">
                        {t(
                          holidayLabelKey(occurrence),
                          { name: holidayName(occurrence) },
                        )}
                      </span>
                    )}
                  </span>
                  {conflict || publicHoliday ? (
                    <span className="text-xs text-slate-600">
                      {t(leftOutKey(occurrence))}
                    </span>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleLeftOut(occurrence.date)}
                    >
                      {included
                        ? t("account.organization_series.leave_out")
                        : t("account.organization_series.include")}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {preview.some((o) => o.holiday) && (
            // The credit the holiday data's licence (ODbL) asks for where it is shown;
            // the same source as HOLIDAY_SOURCE_CREDIT in new-backend/admin holidayController.
            <p className="text-xs text-slate-600">
              {t("account.organization_series.holiday_credit")}{" "}
              <a href="https://www.openholidaysapi.org" target="_blank" rel="noreferrer" className="underline">
                OpenHolidays API
              </a>
              {", "}
              <a href="https://opendatacommons.org/licenses/odbl/" target="_blank" rel="noreferrer" className="underline">
                ODbL
              </a>
            </p>
          )}
          <Button className="w-full" onClick={handleCreate} disabled={toBook.length === 0 || isCreating}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("account.organization_series.book", { count: toBook.length })}
          </Button>
        </div>
      )}

      {backToPlan}
    </AccountPageLayout>
  );
};

export default OrganizationSeriesPage;
